/**
 * V130: MM 메모 개별 편집 모달
 *
 * 업무상세 / 특이사항 / 테스트 결제비용 각 필드를 개별적으로 편집하는 모달
 * - 클릭 시 오픈
 * - 저장: 해당 필드만 patch 저장
 * - 취소: 변경사항 폐기, 서버 요청 없음
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Save } from 'lucide-react';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';

/** 편집 가능한 필드 타입 */
export type MmNoteFieldType = 'workDetails' | 'remarks' | 'costOccurred';

/** 필드별 라벨 및 스타일 */
const FIELD_CONFIG: Record<MmNoteFieldType, { label: string; placeholder: string; color: string }> = {
  workDetails: {
    label: '업무상세',
    placeholder: '업무 상세내역을 입력하세요',
    color: 'var(--color-gray-700)',
  },
  remarks: {
    label: '특이사항',
    placeholder: '특이사항을 입력하세요',
    color: 'var(--color-yellow-700)',
  },
  costOccurred: {
    label: '테스트 결제비용',
    placeholder: '테스트 결제비용 내역을 입력하세요',
    color: 'var(--color-green-700)',
  },
};

interface MmNoteEditModalProps {
  /** 모달 표시 여부 */
  isOpen: boolean;
  /** 편집할 필드 */
  field: MmNoteFieldType;
  /** 프로젝트명 (헤더 표시용) */
  projectName: string;
  /** 현재 값 */
  initialValue: string;
  /** 저장 중 상태 */
  isSaving: boolean;
  /** 저장 핸들러 */
  onSave: (value: string) => Promise<void>;
  /** 닫기 핸들러 */
  onClose: () => void;
}

/**
 * MM 메모 개별 편집 모달
 */
export function MmNoteEditModal({
  isOpen,
  field,
  projectName,
  initialValue,
  isSaving,
  onSave,
  onClose,
}: MmNoteEditModalProps) {
  const [value, setValue] = useState(initialValue);
  const config = FIELD_CONFIG[field];

  // V130.1: 모달 열릴 때 초기값 설정 (field 추가 - 방어적 코딩)
  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
    }
  }, [isOpen, initialValue, field]);

  // V130.1: 저장 핸들러 (중복 요청 방지 추가)
  const handleSave = useCallback(async () => {
    // V130.1: 저장 중이면 early return (이중 방어)
    if (isSaving) return;

    try {
      await onSave(value);
      onClose();
    } catch (err) {
      console.error('[V130] MmNoteEditModal save error:', err);
      // 에러 시에도 모달 유지 (사용자가 재시도 가능)
    }
  }, [value, onSave, onClose, isSaving]);

  // 취소 핸들러 (서버 요청 없음)
  const handleCancel = useCallback(() => {
    setValue(initialValue); // 변경사항 폐기
    onClose();
  }, [initialValue, onClose]);

  // ESC 키 핸들러
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSaving, handleCancel]);

  // textarea 자동 높이 조절
  const autoResizeTextarea = useCallback((element: HTMLTextAreaElement) => {
    element.style.height = 'auto';
    element.style.height = `${Math.max(150, element.scrollHeight)}px`;
  }, []);

  if (!isOpen) return null;

  return (
    <>
      {/* 오버레이 */}
      <ModalOverlay
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
        }}
        onClose={isSaving ? undefined : handleCancel}
      />

      {/* 모달 */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--color-gray-900)',
              }}
            >
              {config.label} 편집
            </h3>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: '14px',
                color: 'var(--color-gray-500)',
              }}
            >
              {projectName}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.5 : 1,
            }}
            title="닫기 (ESC)"
          >
            <X size={20} style={{ color: 'var(--color-gray-500)' }} />
          </button>
        </div>

        {/* 본문 */}
        <div
          style={{
            padding: '20px',
            flex: 1,
            overflow: 'auto',
          }}
        >
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onInput={(e) => autoResizeTextarea(e.currentTarget)}
            ref={(el) => { if (el) autoResizeTextarea(el); }}
            placeholder={config.placeholder}
            disabled={isSaving}
            style={{
              width: '100%',
              minHeight: '150px',
              padding: '12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              fontSize: '16px',
              lineHeight: 1.6,
              resize: 'none',
              overflow: 'hidden',
              color: config.color,
              backgroundColor: isSaving ? 'var(--color-gray-50)' : 'white',
            }}
            autoFocus
          />
        </div>

        {/* 푸터 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '16px 20px',
            borderTop: '1px solid var(--color-gray-200)',
          }}
        >
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            style={{
              padding: '10px 20px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: 'var(--color-gray-700)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.5 : 1,
            }}
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'var(--color-primary)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            <Save size={16} />
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </>
  );
}
